import { useState } from 'react';
import { FileText, StickyNote, Eye, Edit, Trash2, Download, Users } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useDocuments, FamilyDocument } from '@/hooks/useDocuments';
import { useUniversalContacts } from '@/hooks/useUniversalContacts';
import { useFamily } from '@/hooks/useFamily';
import { useAuth } from '@/hooks/useAuth';
import { MultipleAssigneeAvatars } from '@/components/tasks/MultipleAssigneeAvatars';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface DocumentItemProps {
  document: FamilyDocument;
  onClick: () => void;
}

export const DocumentItem = ({ document, onClick }: DocumentItemProps) => {
  const { family } = useFamily();
  const { contacts, getDocumentAssignmentId } = useUniversalContacts(family?.id);
  const { user } = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);

  // Get linked contacts using the new universal contacts system
  const linkedContacts = document.linked_persons 
    ? contacts.filter(contact => 
        document.linked_persons!.includes(getDocumentAssignmentId(contact))
      )
    : [];

  const categoryColors = {
    medical: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
    identity: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
    school: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
    insurance: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400',
    other: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
  };

  const categoryLabels = {
    medical: 'Médical',
    identity: 'Identité',
    school: 'École',
    insurance: 'Assurance',
    other: 'Autre'
  };

  return (
    <Card 
      className="border border-panel bg-panel hover:bg-panel-hover transition-all duration-200 cursor-pointer"
      onClick={onClick}
    >
      <CardContent className={`${document.document_type === 'note' ? 'p-3' : 'p-4'}`}>
        <div className="flex items-start justify-between">
          {/* Left: Icon and content */}
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`p-2 rounded-lg ${
              document.document_type === 'document' 
                ? 'bg-violet-pastel/20 text-violet-pastel' 
                : 'bg-violet-pastel/15 text-violet-pastel'
            }`}>
              {document.document_type === 'document' ? (
                <FileText className="h-5 w-5" />
              ) : (
                <StickyNote className="h-5 w-5" />
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 space-y-1">
              <div>
                <h3 className="font-semibold text-sm leading-tight break-words line-clamp-2">{document.title}</h3>
              </div>

              {document.description && document.document_type === 'document' && (
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {document.description}
                </p>
              )}

            </div>
          </div>

          {/* Right: Avatars */}
          {linkedContacts.length > 0 && (
            <div className="flex-shrink-0 ml-3">
              <MultipleAssigneeAvatars assignedContacts={linkedContacts} size="sm" maxVisible={3} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};